import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { refreshConnectorCapabilities } from "../../../api/connectorsApi";
import type { AgentRoute, ApiKeyRoute } from "../../../api/threads/liveMessages";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import { FORM_HELP_TEXT_CLASS } from "../../../ui/styles/formStyles";

/** How often, and how many times, the options are read again while the PC looks. */
const RELOAD_INTERVAL_MILLISECONDS = 3_000;
const MAXIMUM_RELOADS = 20;

interface RefreshFactsButtonProps {
  connectorId: string;
  route: AgentRoute | ApiKeyRoute | null;
  /** When the connector's report the options come from arrived; a newer one ends the wait. */
  reportReceivedAt: string | null;
  /** Reads the route options again, keeping the current ones on screen. */
  onReload: () => void;
}

/**
 * Asks the host's PC to read its agents' models and model catalogues and the API listings
 * again (capabilities.md, D17), then reads the route options again until its new report
 * arrives. No model is asked anything; an agent opens one session without a prompt.
 */
export default function RefreshFactsButton({ connectorId, route, reportReceivedAt, onReload }: RefreshFactsButtonProps) {
  const { t } = useTranslation();
  const { state, submit } = useSubmission();
  // The report the options came from when the host asked: the wait ends with a newer one.
  const [askedAt, setAskedAt] = useState<string | null | undefined>(undefined);
  const waiting = state.status === "succeeded" && askedAt === reportReceivedAt;

  useEffect(() => {
    if (!waiting) return;
    let reloads = 0;
    const timer = setInterval(() => {
      reloads += 1;
      onReload();
      if (reloads >= MAXIMUM_RELOADS) {
        clearInterval(timer);
        setAskedAt(undefined);
      }
    }, RELOAD_INTERVAL_MILLISECONDS);
    return () => { clearInterval(timer); };
  }, [waiting, onReload]);

  const ask = (): void => {
    setAskedAt(reportReceivedAt);
    submit(() => refreshConnectorCapabilities(connectorId, route?.kind === "agent" ? route.agent_id : null));
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <AppButton variant="secondary" size="xs" disabled={state.status === "submitting" || waiting} onClick={ask}>
          {t("threads.hosting.facts.refresh")}
        </AppButton>
        <span className={FORM_HELP_TEXT_CLASS}>{waiting ? t("threads.hosting.facts.refreshing") : t("threads.hosting.facts.refreshHint")}</span>
      </div>
      {state.status === "failed" ? <ErrorNotice error={state.error} compact /> : null}
    </div>
  );
}
